/**
 * Admin 共享表格：保留原生表格结构和 .tbl 设计样式，提供列宽拖动与键盘调整。
 * 首次调整时冻结当前列宽，只改变目标列；列结构变化后恢复该结构的默认布局。
 */
import {
  type CSSProperties,
  Children,
  type ComponentPropsWithoutRef,
  Fragment,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import './Table.css';

type ElementProps = {
  children?: ReactNode;
  style?: CSSProperties;
  colSpan?: number;
  rowSpan?: number;
  'aria-label'?: string;
};
type WidthState = { signature: string; widths: number[] };
type DragState = {
  index: number;
  x: number;
  widths: number[];
  min: number;
  pointerId: number;
  previous: WidthState | null;
};

function headerText(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      return isValidElement<ElementProps>(child) ? headerText(child.props.children) : '';
    })
    .join('')
    .trim();
}

/** 只访问表头及 Fragment，避免把正文里嵌套表格的列混入当前表。 */
function mapHeaders(
  nodes: ReactNode,
  map: (node: ReactElement<ElementProps>) => ReactNode,
): ReactNode {
  return Children.map(nodes, (node) => {
    if (!isValidElement<ElementProps>(node)) return node;
    if (node.type === 'th') return map(node);
    if (node.type === Fragment || node.type === 'thead' || node.type === 'tr') {
      return cloneElement(node, { children: mapHeaders(node.props.children, map) });
    }
    return node;
  });
}

function headerRowCount(nodes: ReactNode): number {
  return Children.toArray(nodes).reduce<number>((count, node) => {
    if (!isValidElement<ElementProps>(node)) return count;
    if (node.type === 'tr') return count + 1;
    return node.type === Fragment || node.type === 'thead'
      ? count + headerRowCount(node.props.children)
      : count;
  }, 0);
}

export function Table({
  children,
  className = '',
  style,
  minColumnWidth = 64,
  resizable = true,
  ...props
}: ComponentPropsWithoutRef<'table'> & { minColumnWidth?: number; resizable?: boolean }) {
  const tableRef = useRef<HTMLTableElement>(null);
  const drag = useRef<DragState | null>(null);
  const minimumWidths = useRef<{ signature: string; widths: number[] } | null>(null);
  const [sizing, setSizing] = useState<WidthState | null>(null);
  const labels: string[] = [];
  let simpleHeaders = true;
  mapHeaders(children, (node) => {
    labels.push(headerText(node.props.children));
    if ((node.props.colSpan ?? 1) !== 1 || (node.props.rowSpan ?? 1) !== 1) simpleHeaders = false;
    return node;
  });
  const signature = JSON.stringify(labels);
  const widths = sizing?.signature === signature ? sizing.widths : null;
  const enabled = resizable && simpleHeaders && labels.length > 0 && headerRowCount(children) === 1;

  useEffect(() => {
    if (signature === '[]') return;
    const table = tableRef.current;
    const header = table?.querySelector('thead');
    if (!table || !header) return;
    let offset = 0;
    let frame: number | null = null;
    // 横向 overflow 会截断 CSS sticky 的纵向祖先，按实际页面/弹窗滚动面补偿表头。
    const update = () => {
      frame = null;
      let top = 0;
      let ancestor = table.parentElement?.parentElement;
      while (ancestor && ancestor !== document.documentElement) {
        if (
          /auto|scroll/.test(getComputedStyle(ancestor).overflowY) &&
          ancestor.scrollHeight > ancestor.clientHeight
        ) {
          top = ancestor.getBoundingClientRect().top + ancestor.clientTop;
          break;
        }
        ancestor = ancestor.parentElement;
      }
      const headerRect = header.getBoundingClientRect();
      const naturalTop = headerRect.top - offset;
      const next = Math.max(
        0,
        Math.min(
          top - naturalTop,
          table.getBoundingClientRect().bottom - naturalTop - headerRect.height,
        ),
      );
      if (offset !== next) {
        offset = next;
        table.style.setProperty('--table-header-offset', `${offset}px`);
      }
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    update();
    document.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(table);
    return () => {
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      table.style.removeProperty('--table-header-offset');
    };
  }, [signature]);

  const measureWidths = (): number[] => {
    if (widths) return [...widths];
    const table = tableRef.current;
    const cols = table?.querySelectorAll(':scope > colgroup > col');
    return Array.from(
      table?.querySelectorAll<HTMLTableCellElement>(':scope > thead > tr > th') ?? [],
      (cell, index) => {
        const declared = Number.parseFloat(
          (cols?.[index] as HTMLElement | undefined)?.style.width || cell.style.width,
        );
        return cell.getBoundingClientRect().width || declared || 120;
      },
    );
  };

  const minimumFor = (index: number, measured: number[]) => {
    if (minimumWidths.current?.signature !== signature) {
      minimumWidths.current = {
        signature,
        widths: measured.map((width) => Math.min(minColumnWidth, width)),
      };
    }
    return minimumWidths.current.widths[index] ?? minColumnWidth;
  };

  useEffect(() => {
    setSizing((current) => (current?.signature === signature ? current : null));
    const finish = (cancel = false) => {
      if (!drag.current) return;
      if (cancel) setSizing(drag.current.previous);
      drag.current = null;
      document.documentElement.classList.remove('is-resizing-table');
    };
    const move = (event: PointerEvent) => {
      const active = drag.current;
      if (!active || event.pointerId !== active.pointerId) return;
      event.preventDefault();
      const next = [...active.widths];
      next[active.index] = Math.max(
        active.min,
        Math.round((active.widths[active.index] ?? 120) + event.clientX - active.x),
      );
      setSizing({ signature, widths: next });
    };
    const up = (event: PointerEvent) => {
      if (event.pointerId === drag.current?.pointerId) finish();
    };
    const cancel = () => finish(true);
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && drag.current) {
        event.stopPropagation();
        finish(true);
      }
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    window.addEventListener('keydown', key, true);
    return () => {
      finish();
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
      window.removeEventListener('keydown', key, true);
    };
  }, [signature]);

  let headerIndex = 0;
  const content = enabled
    ? mapHeaders(children, (node) => {
        const index = headerIndex++;
        const label = labels[index] || `第 ${index + 1} 列`;
        return cloneElement(node, {
          'aria-label': node.props['aria-label'] ?? (labels[index] || undefined),
          children: (
            <>
              {node.props.children}
              <button
                type="button"
                role="separator"
                aria-orientation="vertical"
                aria-valuemin={
                  minimumWidths.current?.signature === signature
                    ? minimumWidths.current.widths[index]
                    : 0
                }
                aria-valuemax={Math.max(10000, widths?.[index] ?? 0)}
                aria-valuenow={widths?.[index] ?? (Number(node.props.style?.width) || 120)}
                aria-valuetext={widths ? `${widths[index]} 像素` : '自动列宽，使用方向键调整'}
                className="table-column-resize"
                aria-label={`调整${label}列宽`}
                title="拖动调整列宽；方向键微调；双击恢复默认"
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  minimumWidths.current = null;
                  setSizing(null);
                }}
                onPointerDown={(event) => {
                  if (event.button !== 0) return;
                  event.preventDefault();
                  event.stopPropagation();
                  const measured = measureWidths();
                  drag.current = {
                    index,
                    x: event.clientX,
                    widths: measured,
                    min: minimumFor(index, measured),
                    pointerId: event.pointerId,
                    previous: sizing,
                  };
                  setSizing({ signature, widths: measured });
                  document.documentElement.classList.add('is-resizing-table');
                  event.currentTarget.setPointerCapture?.(event.pointerId);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                  event.preventDefault();
                  event.stopPropagation();
                  const measured = measureWidths();
                  const original = measured[index] ?? 120;
                  const change = (event.shiftKey ? 50 : 10) * (event.key === 'ArrowRight' ? 1 : -1);
                  measured[index] = Math.max(minimumFor(index, measured), original + change);
                  setSizing({ signature, widths: measured });
                }}
              />
            </>
          ),
        });
      })
    : children;
  // 调整后的 colgroup 由 React 持有，避免分页刷新或表头重渲染覆盖拖动结果。
  const renderedChildren = Children.map(content, (node) =>
    widths && enabled && isValidElement(node) && node.type === 'colgroup' ? null : node,
  );
  const totalWidth = widths?.reduce((sum, width) => sum + width, 0);
  return (
    <div className="table-scroll">
      <table
        {...props}
        ref={tableRef}
        className={[
          'tbl',
          enabled ? 'resizable-table' : '',
          ...className.split(/\s+/).filter((name) => name !== 'tbl'),
        ]
          .filter(Boolean)
          .join(' ')}
        data-columns-sized={widths && enabled ? 'true' : undefined}
        style={
          widths && enabled
            ? { ...style, tableLayout: 'fixed', width: totalWidth, minWidth: totalWidth }
            : style
        }
      >
        {widths && enabled && (
          <colgroup>
            {widths.map((width, index) => (
              <col key={`${signature}-${index}`} style={{ width }} />
            ))}
          </colgroup>
        )}
        {renderedChildren}
      </table>
    </div>
  );
}
